import React from 'react';
import { HelpCircle, Bell, User, Sparkles } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { supabase } from '../lib/supabase';
import { faqService, FaqItem } from '../services/faqService';
import { getNotificationChatRoom, getNotificationLink } from '../services/notificationService';
import { checkDeadlineReminders } from '../services/deadlineReminderService';
import { checkProjectBudgets, checkRunningTimer } from '../services/timeAlertService';
import { useModuleAccess } from '../contexts/ModuleAccessContext';
import { NotificationIcon, formatNotificationDate, notificationColor } from './Notifications/notificationVisuals';
import { PREFERENCES_CHANGED_EVENT, getDisabledTypes } from '../services/notificationPreferences';
import { clickableProps } from '../utils/a11y';

interface UserProfile {
  name: string | null;
  email: string | null;
  user_photo: string | null;
}

interface HeaderProps {
  activeSection?: string;
  onSectionChange?: (section: string) => void;
}

const Header: React.FC<HeaderProps> = ({ activeSection, onSectionChange = () => {} }) => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { organizationId } = useOrganization();
  const { isAdmin, loading: accessLoading } = useModuleAccess();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [disabledTypes, setDisabledTypes] = useState<Set<string>>(new Set());
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [topFaqs, setTopFaqs] = useState<FaqItem[]>([]);
  const [loadingFaqs, setLoadingFaqs] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const helpRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user?.id) return;
      
      try {
        const { data, error } = await supabase
          .from('user_profile')
          .select('name, email, user_photo')
          .eq('auth_id', user.id)
          .single();
        
        if (error) throw error;
        if (data) setUserProfile(data);
      } catch (err) {
        console.error('Erro ao carregar perfil do usuário:', err);
      }
    };
    
    fetchUserProfile();
    // Depende de `user?.id`, não do objeto `user` (ver App.tsx) — evita refazer
    // a busca toda vez que a aba volta a ficar visível.
  }, [user?.id]);

  useEffect(() => {
    const fetchNotifications = async (silencioso = false) => {
      if (!user?.id || !organizationId) return;
      if (!silencioso) setLoadingNotifications(true);
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });
      if (error) {
        console.error('Erro ao buscar notificações:', error);
        setLoadingNotifications(false);
        return;
      }
      setNotifications(data || []);
      setLoadingNotifications(false);
    };
    fetchNotifications();

    if (!user?.id || !organizationId) return;

    // Lembretes de prazo e cronômetro esquecido entram como notificações novas
    // e chegam pelo realtime abaixo. Prazo checa uma vez por dia; o cronômetro
    // a cada 30 min, porque passa do limite com o app aberto.
    void checkDeadlineReminders(organizationId, user.id);
    void checkRunningTimer(organizationId, user.id);
    const intervaloTimer = window.setInterval(
      () => void checkRunningTimer(organizationId, user.id),
      30 * 60 * 1000
    );

    // Sem isto a notificação só aparecia pra quem recebe depois de um F5: o
    // Header não desmonta ao navegar, então a busca acima roda uma vez só.
    const channel = supabase
      .channel(`notifications-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          const nova = payload.new as { id: string; organization_id: string };
          if (nova.organization_id !== organizationId) return;
          setNotifications(prev =>
            prev.some(n => n.id === nova.id) ? prev : [nova, ...prev]
          );
        }
      )
      .subscribe();

    // Rede de segurança se o realtime cair: ao voltar pra aba, rebusca.
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      fetchNotifications(true);
      // Aba aberta de um dia pro outro: a checagem diária roda ao voltar.
      void checkDeadlineReminders(organizationId, user.id);
      void checkRunningTimer(organizationId, user.id);
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // A página de Notificações marca e exclui por conta própria; rebusca aqui.
    const handleUpdated = () => fetchNotifications(true);
    window.addEventListener('notificationsUpdated', handleUpdated);

    return () => {
      window.removeEventListener('notificationsUpdated', handleUpdated);
      supabase.removeChannel(channel);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.clearInterval(intervaloTimer);
    };
  }, [user?.id, organizationId]);

  // Horas previstas estourando: aviso só para administradores, uma vez por dia.
  useEffect(() => {
    if (accessLoading || !isAdmin || !user?.id || !organizationId) return;
    void checkProjectBudgets(organizationId, user.id);
  }, [accessLoading, isAdmin, user?.id, organizationId]);

  // Categorias que a pessoa desligou em Notificações > Preferências somem do sino.
  useEffect(() => {
    if (!user?.id || !organizationId) return;
    let cancelado = false;
    const carregar = () => {
      getDisabledTypes(organizationId, user.id).then(tipos => {
        if (!cancelado) setDisabledTypes(tipos);
      });
    };
    carregar();
    window.addEventListener(PREFERENCES_CHANGED_EVENT, carregar);
    return () => {
      cancelado = true;
      window.removeEventListener(PREFERENCES_CHANGED_EVENT, carregar);
    };
  }, [user?.id, organizationId]);

  const visibleNotifications = notifications.filter(n => !disabledTypes.has(n.type));
  const unreadCount = visibleNotifications.filter(n => !n.read).length;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setShowProfileMenu(false);
      }
      if (helpRef.current && !helpRef.current.contains(event.target as Node)) {
        setShowHelp(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Carrega as perguntas mais vistas só quando o dropdown de ajuda é aberto
  useEffect(() => {
    if (!showHelp || !organizationId || topFaqs.length > 0) return;

    const fetchTopFaqs = async () => {
      setLoadingFaqs(true);
      try {
        const items = await faqService.getFaqItems(organizationId);
        const sorted = [...items]
          .filter(item => item.is_active)
          .sort((a, b) => b.views_count - a.views_count)
          .slice(0, 4);
        setTopFaqs(sorted);
      } catch (err) {
        console.error('Erro ao carregar FAQs em destaque:', err);
      } finally {
        setLoadingFaqs(false);
      }
    };

    fetchTopFaqs();
  }, [showHelp, organizationId, topFaqs.length]);

  const handleNotificationClick = async (notificacao: {
    id: string;
    read?: boolean;
    type?: string;
    metadata?: Record<string, unknown> | null;
  }) => {
    const link = getNotificationLink(notificacao);
    const salaDoChat = getNotificationChatRoom(notificacao);
    if (link) {
      setShowNotifications(false);
      navigate(link);
    } else if (salaDoChat) {
      // O chat é um popup, não uma rota: quem abre a sala é o
      // ChatToastNotifications, que vive dentro do ChatProvider.
      setShowNotifications(false);
      window.dispatchEvent(new CustomEvent('chat:open-room', { detail: { roomId: salaDoChat } }));
    }

    if (notificacao.read) return;
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('id', notificacao.id);
    if (error) {
      console.error('Erro ao marcar notificação como lida:', error);
    }
    setNotifications(prev => prev.map(notification =>
      notification.id === notificacao.id ? { ...notification, read: true } : notification
    ));
  };

  const handleMarkAllAsRead = async () => {
    const unreadIds = visibleNotifications.filter(n => !n.read).map(n => n.id);
    if (unreadIds.length === 0) return;
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .in('id', unreadIds);
    if (error) {
      console.error('Erro ao marcar todas notificações como lidas:', error);
    }
    const marcadas = new Set(unreadIds);
    setNotifications(prev => prev.map(notification =>
      marcadas.has(notification.id) ? { ...notification, read: true } : notification
    ));
  };

  const getTitle = () => {
    switch (activeSection) {
      case 'projects':
        return '';
      case 'team':
        return 'Time';
      case 'settings':
        return 'Configurações';
      case 'calendar':
        return '';
      case 'help-popup':
        return 'Central de Ajuda';
      case 'help':
        return 'Ajuda e Suporte';
      default:
        return '';
    }
  };

  const handleViewAll = () => {
    setShowNotifications(false);
    onSectionChange('company-notifications');
    window.dispatchEvent(new Event('notificationsUpdated'));
  };

  return (
    <div className="sticky top-0 z-40 flex items-center justify-between p-4 md:p-6 border-b bg-white">
      <div>
        <h1 className="text-xl font-semibold">{getTitle()}</h1>
      </div>
      
      <div className="flex items-center gap-6">
        <div className="relative" ref={helpRef}>
          <button
            className="p-2 hover:bg-gray-100 rounded-full"
            onClick={() => setShowHelp(!showHelp)}
            title="Ajuda e Suporte"
            aria-label="Ajuda e Suporte"
          >
            <HelpCircle className="w-5 h-5 text-gray-500" />
          </button>

          {/* Help Dropdown */}
          <div className={`absolute right-0 mt-3 w-[360px] bg-white border border-gray-200 rounded-2xl shadow-[var(--shadow-3)] transition-all duration-300 transform ${showHelp ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'} z-50`}>
            <div className="px-6 py-4 border-b border-gray-100 bg-coral-soft/20 rounded-t-2xl">
              <h3 className="text-lg font-semibold text-gray-900">Ajuda e Suporte</h3>
              <p className="text-sm text-gray-500 mt-0.5">Perguntas mais frequentes</p>
            </div>

            <div className="max-h-[320px] overflow-y-auto custom-scrollbar">
              {loadingFaqs ? (
                <div className="flex items-center justify-center py-10">
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-coral"></div>
                </div>
              ) : topFaqs.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 px-6 text-center">
                  <HelpCircle className="w-8 h-8 text-gray-300 mb-2" />
                  <p className="text-gray-500 text-sm">Nenhuma pergunta cadastrada ainda.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {topFaqs.map((faq) => (
                    <button
                      key={faq.id}
                      onClick={() => {
                        setShowHelp(false);
                        onSectionChange('help-popup');
                      }}
                      className="w-full text-left px-6 py-3 hover:bg-gray-50 transition-colors"
                    >
                      <p className="text-sm font-medium text-gray-900 line-clamp-2">{faq.question}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="px-6 py-3 border-t border-gray-100 rounded-b-2xl">
              <button
                onClick={() => {
                  setShowHelp(false);
                  onSectionChange('help-popup');
                }}
                className="w-full text-center text-sm font-medium text-coral hover:text-coral-deep transition-colors"
              >
                Ver Central de Ajuda completa
              </button>
            </div>
          </div>
        </div>

        <button
          className="p-2 hover:bg-gray-100 rounded-full"
          onClick={() => window.dispatchEvent(new CustomEvent('nankin-ai:toggle'))}
          title="Assistente (Ctrl+K)"
          aria-label="Assistente"
        >
          <Sparkles className="w-5 h-5 text-gray-500" />
        </button>

        <div className="relative group">
          <button
            className="p-2.5 hover:bg-gray-100 rounded-xl relative transition-all duration-200 hover:scale-105"
            onClick={() => setShowNotifications(!showNotifications)}
            aria-label={unreadCount > 0 ? `Notificações: ${unreadCount} não lidas` : 'Notificações'}
          >
            <Bell className="w-5 h-5 text-gray-600" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-nk-error text-white text-xs rounded-full w-5 h-5 flex items-center justify-center shadow-[var(--shadow-1)] animate-pulse">
                {unreadCount}
              </span>
            )}
          </button>
          
          {/* Notification Dropdown */}
          <div className={`absolute right-0 mt-3 w-[420px] bg-white border border-gray-200 rounded-2xl shadow-[var(--shadow-3)] transition-all duration-300 transform ${showNotifications ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'} z-50`}>
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100 bg-coral-soft/20 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Notificações</h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {unreadCount > 0 
                      ? `${unreadCount} nova${unreadCount > 1 ? 's' : ''}`
                      : 'Nenhuma nova notificação'
                    }
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {unreadCount > 0 && (
                    <button 
                      onClick={handleMarkAllAsRead}
                      className="text-xs text-coral hover:text-coral-deep bg-coral-soft/40 hover:bg-coral-soft/60 px-3 py-1.5 rounded-lg transition-all duration-200 font-medium"
                    >
                      Marcar todas como lidas
                    </button>
                  )}
                  <button 
                    onClick={handleViewAll}
                    className="text-xs text-gray-600 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-lg transition-all duration-200 font-medium"
                  >
                    Ver todas
                  </button>
                </div>
              </div>
            </div>

            {/* Notifications List */}
            <div className="max-h-[420px] overflow-y-auto custom-scrollbar">
              {loadingNotifications ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-coral"></div>
                </div>
              ) : visibleNotifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-6">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <Bell className="w-8 h-8 text-gray-400" />
                  </div>
                  <h4 className="text-gray-900 font-medium mb-2">Nenhuma notificação</h4>
                  <p className="text-gray-500 text-sm text-center">
                    Você está em dia! Não há notificações pendentes no momento.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {visibleNotifications.slice(0, 5).map((notification) => (
                    <div 
                      key={notification.id}
                      className={`px-6 py-4 hover:bg-gray-50 cursor-pointer transition-all duration-200 group relative ${
                        !notification.read ? 'bg-coral-soft/20 border-l-4 border-coral' : ''
                      }`}
                      {...clickableProps}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="flex items-start gap-4">
                        {/* Icon/Avatar */}
                        {notification.user ? (
                          <div className="w-10 h-10 rounded-full bg-ink-400 flex items-center justify-center text-sm font-semibold text-white shadow-[var(--shadow-1)]">
                            {notification.user.avatar || notification.user.name?.charAt(0).toUpperCase()}
                          </div>
                        ) : (
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-[var(--shadow-1)] ${notificationColor(notification.type)}`}>
                            <NotificationIcon type={notification.type} />
                          </div>
                        )}
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              {notification.user && (
                                <span className="font-semibold text-gray-900 text-sm">
                                  {notification.user.name}
                                </span>
                              )}
                              <p className={`text-gray-900 text-sm font-medium ${notification.user ? 'mt-0.5' : ''} leading-relaxed`}>
                                {notification.title}
                              </p>
                              {notification.content && (
                                <p className="text-gray-600 text-xs mt-0.5 leading-relaxed line-clamp-2">
                                  {notification.content}
                                </p>
                              )}
                              <div className="flex items-center gap-2 mt-2">
                                <span className="text-xs text-gray-500">
                                  {formatNotificationDate(notification.created_at ?? notification.timestamp) || ' '}
                                </span>
                                {!notification.read && (
                                  <span className="w-2 h-2 bg-coral rounded-full"></span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Action Button */}
                          {notification.actionButton && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                notification.actionButton?.action();
                              }}
                              className="mt-3 px-4 py-2 text-sm bg-coral-deep text-white rounded-lg hover:bg-coral-press transition-all duration-200 shadow-[var(--shadow-1)] hover:shadow-[var(--shadow-2)] font-medium"
                            >
                              {notification.actionButton.label}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {visibleNotifications.length > 5 && (
                    <div className="px-6 py-4 bg-gray-50 rounded-b-2xl">
                      <button 
                        onClick={handleViewAll}
                        className="w-full text-center text-sm text-coral hover:text-coral-deep font-medium py-2 hover:bg-coral-soft/30 rounded-lg transition-all duration-200"
                      >
                        Ver mais {visibleNotifications.length - 5} notificações
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="relative" ref={profileRef}>
          <button 
            className="p-1 hover:bg-gray-100 rounded-full"
            onClick={() => setShowProfileMenu(!showProfileMenu)}
          >
            {userProfile?.user_photo ? (
              <img
                src={userProfile.user_photo}
                alt="User"
                className="w-8 h-8 rounded-full object-cover"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-coral-deep flex items-center justify-center text-white">
                {userProfile?.name ? userProfile.name.charAt(0).toUpperCase() : 'U'}
              </div>
            )}
          </button>
          <div className={`absolute right-0 mt-2 w-64 bg-white border rounded-lg shadow-[var(--shadow-2)] py-2 ${showProfileMenu ? 'block' : 'hidden'}`}>
            <div className="px-4 py-2 text-sm">
              <p className="font-medium">{userProfile?.name || user?.email || 'Usuário'}</p>
              <p className="text-gray-500 break-words">{userProfile?.email || user?.email || 'Email não disponível'}</p>
            </div>
            <div className="border-t mt-2 pt-2">
              <div 
                onClick={() => {
                  setShowProfileMenu(false);
                  onSectionChange('profile');
                }}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
              >
                Meu Perfil
              </div>
              <div 
                onClick={async () => {
                  try {
                    await signOut();
                    navigate('/signin');
                  } catch (error) {
                    console.error('Error signing out:', error);
                  }
                }}
                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 cursor-pointer"
              >
                Sair
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;